import "react-day-picker/style.css";

import useClickOutside from "@admin/hooks/useClickOutside";
import { useLanguageStore } from "@enos-monorepo/lib";
import {
  addWeeks,
  endOfWeek,
  getISOWeek,
  isBefore,
  Locale,
  startOfWeek,
  subWeeks,
} from "date-fns";
import { da, enUS, tr } from "date-fns/locale";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { DateRange, DayPicker, Matcher } from "react-day-picker";
import { useTranslation } from "react-i18next";

function getDateOfWeek(w: number, y: number) {
  const d = 1 + (w - 1) * 7; // 1st of January + 7 days for each week

  return new Date(y, 0, d);
}

interface WeekPickerProps {
  setSelectedWeek: Dispatch<SetStateAction<DateRange | undefined>>;
  disabledDate?: Matcher;
  isButtonShown?: boolean;
  menuOrientation?: "left" | "right";
  autofill?: boolean;
  initialPreviousWeek?: boolean;
}
const locales: Record<string, Locale> = {
  en: enUS,
  da: da,
  tr: tr,
};

export function WeekPicker({
  setSelectedWeek,
  disabledDate,
  isButtonShown = true,
  menuOrientation = "left",
  autofill = true,
  initialPreviousWeek = false,
  ...props
}: WeekPickerProps) {
  const { t } = useTranslation();
  const { language } = useLanguageStore();
  const mappedLanguage = locales[language ?? ""] || enUS;
  const [show, setShow] = useState(false);
  const [selectedWeekRange, setSelectedWeekRange] = useState<DateRange>();

  const ref = useRef<HTMLDivElement | null>(null);

  const goToPreviousWeek = () => {
    if (!selectedWeekRange || !selectedWeekRange.from || !selectedWeekRange.to)
      return;

    const previousWeek = {
      from: subWeeks(selectedWeekRange.from, 1),
      to: subWeeks(selectedWeekRange.to, 1),
    };

    if (
      isBefore(previousWeek.from, startOfWeek(new Date(), { weekStartsOn: 1 }))
    ) {
      return;
    }

    setSelectedWeekRange(previousWeek);
  };

  useEffect(() => {
    setSelectedWeek(selectedWeekRange);
  }, [selectedWeekRange, setSelectedWeek]);

  const goToNextWeek = () => {
    if (!selectedWeekRange || !selectedWeekRange.from || !selectedWeekRange.to)
      return;

    const nextWeek = {
      from: addWeeks(selectedWeekRange.from, 1),
      to: addWeeks(selectedWeekRange.to, 1),
    };

    setSelectedWeekRange(nextWeek);
  };

  useEffect(() => {
    if (autofill) {
      const today = new Date();
      const startWeek = initialPreviousWeek ? subWeeks(today, 1) : today;

      const initialWeekRange = {
        from: startOfWeek(startWeek, { weekStartsOn: 1 }),
        to: endOfWeek(startWeek, { weekStartsOn: 1 }),
      };

      setSelectedWeekRange(initialWeekRange);
    }
  }, [autofill, initialPreviousWeek]);

  useClickOutside(ref, () => setShow(false));

  return (
    <div className="relative">
      <div className="flex flex-row gap-2 w-fit items-center">
        {isButtonShown && (
          <i
            onClick={goToPreviousWeek}
            className="ki-filled ki-left-square text-2xl cursor-pointer"
          ></i>
        )}
        <div
          onClick={() => setShow((prev) => !prev)}
          className="select flex items-center select-sm pr-24 w-fit text-gray-700 rounded-md bg-light border border-gray-300"
        >
          <span>
            {" "}
            {selectedWeekRange?.from
              ? `${t("common.Week")} ${getISOWeek(selectedWeekRange?.from)}`
              : t("common.Select Week")}{" "}
          </span>
        </div>
        {isButtonShown && (
          <i
            onClick={goToNextWeek}
            className="ki-filled ki-right-square text-2xl cursor-pointer"
          ></i>
        )}
      </div>

      {show && (
        <div
          ref={ref}
          className={`card absolute z-30 bg-white p-4 ${menuOrientation === "right" ? "left-0" : "right-0"}`}
        >
          <DayPicker
            locale={mappedLanguage}
            classNames={{
              day: "text-sm",
              focused: "bg-blue-100",
              selected: "bg-blue-100 text-blue-500",
              disabled: "text-gray-300",
              day_button: "text-sm font-semibold rounded-full w-8 h-6",
              today: "text-blue-500",
              range_start: "bg-blue-100 text-blue-500",
              range_end: "bg-blue-100 text-blue-500 rounded-r-md",
              weekday: "text-sm font-semibold text-gray-700",
              chevron: "text-blue-500",
            }}
            components={{
              WeekNumberHeader: () => (
                <div className="text-gray-700 text-sm items-center justify-center flex w-8 h-8 font-semibold">
                  Wk
                </div>
              ),
              WeekNumber: ({ week }) => {
                const isPast = isBefore(
                  getDateOfWeek(
                    week.weekNumber,
                    week.days[0].date.getFullYear(),
                  ),
                  startOfWeek(new Date(), { weekStartsOn: 1 }),
                );

                const isActiveWeek =
                  selectedWeekRange?.from &&
                  selectedWeekRange?.to &&
                  week.weekNumber === getISOWeek(selectedWeekRange.from) &&
                  week.weekNumber === getISOWeek(selectedWeekRange.to);

                return (
                  <button
                    type="button"
                    disabled={isPast}
                    className={`text-sm font-semibold rounded-l-md w-8 h-8 border-r border-gray-300
                      ${
                        isActiveWeek
                          ? "bg-blue-500 text-white"
                          : isPast
                            ? "text-gray-300"
                            : ""
                      }`}
                    onClick={() => {
                      if (isPast) return;
                      const year = week.days[0].date.getFullYear();
                      setSelectedWeekRange({
                        from: startOfWeek(
                          getDateOfWeek(week.weekNumber, year),
                          { weekStartsOn: 1 },
                        ),
                        to: endOfWeek(getDateOfWeek(week.weekNumber, year), {
                          weekStartsOn: 1,
                        }),
                      });
                    }}
                  >
                    {week.weekNumber}
                  </button>
                );
              },

              Chevron: ({ orientation }) => {
                return {
                  left: (
                    <i className="ki-filled ki-left text-xl font-bold text-blue-500" />
                  ),
                  right: (
                    <i className="ki-filled ki-right text-xl font-bold text-blue-500" />
                  ),
                  down: (
                    <i className="ki-filled ki-down text-xl font-bold text-blue-500" />
                  ),
                  up: (
                    <i className="ki-filled ki-up text-xl font-bold text-blue-500" />
                  ),
                }[orientation ?? "right"];
              },
            }}
            showWeekNumber
            showOutsideDays
            weekStartsOn={1}
            ISOWeek
            disabled={disabledDate}
            modifiers={{
              selected: selectedWeekRange,
              range_start: selectedWeekRange?.from,
              range_end: selectedWeekRange?.to,
            }}
            onDayClick={(day, modifiers) => {
              if (modifiers.selected) {
                setSelectedWeekRange(undefined);
                return;
              }
              setSelectedWeekRange({
                from: startOfWeek(day, { weekStartsOn: 1 }),
                to: endOfWeek(day, { weekStartsOn: 1 }),
              });
            }}
          />
        </div>
      )}
    </div>
  );
}
